import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  Typography,
  Switch,
  Select,
  Radio,
  Input,
  Upload,
  Progress,
  Image,
  App,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { handleSubmitFormResponseManage, customUpload } from "../../../utils";
import { useTiendaColegios } from "../../../hooks/useTiendaColegios";
import { useTiendaPaquetes } from "../../../hooks/useTiendaPaquetes";
import {
  createTiendaPaquete,
  updateTiendaPaquete,
} from "../../../services/tienda-paquetes/tienda-paquete.service";
import {
  CreateTiendaPaqueteDto,
  NivelTienda,
} from "../../../services/tienda-paquetes/tienda-paquete.types";

const { Title } = Typography;
const { TextArea } = Input;

interface FormValues {
  school_id?: number;
  nivel: NivelTienda;
  grado: number;
  titulo: string;
  descripcion: string;
  precio: number;
  imagen_url?: string;
  activo: boolean;
}

const initialValues: FormValues = {
  school_id: undefined,
  nivel: "PRIMARIA",
  grado: 1,
  titulo: "",
  descripcion: "",
  precio: 0,
  imagen_url: undefined,
  activo: true,
};

const TiendaPaqueteForm: React.FC = () => {
  const { notification } = App.useApp();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paqueteId = location?.state as number | undefined;
  const [form] = Form.useForm<FormValues>();
  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const [loading, setLoading] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [progressImg, setProgressImg] = useState(0);
  const { tiendaColegios } = useTiendaColegios();
  const { getTiendaPaqueteById } = useTiendaPaquetes();

  useEffect(() => {
    if (paqueteId) {
      setLoading(true);
      getTiendaPaqueteById(paqueteId).then((data) => {
        if (data) {
          const vals: FormValues = {
            school_id: data.school_id,
            nivel: data.nivel,
            grado: data.grado,
            titulo: data.titulo,
            descripcion: data.descripcion,
            precio: data.precio,
            imagen_url: data.imagen_url ?? undefined,
            activo: data.activo,
          };
          form.setFieldsValue(vals);
          setFormValues(vals);
        }
        setLoading(false);
      });
    }
  }, [paqueteId]);

  const handleSubmit = async () => {
    const values = form.getFieldsValue();
    const dto: CreateTiendaPaqueteDto = {
      school_id: values.school_id!,
      nivel: values.nivel,
      grado: values.grado,
      titulo: values.titulo,
      descripcion: values.descripcion,
      precio: values.precio,
      imagen_url: values.imagen_url,
      activo: values.activo,
    };

    await handleSubmitFormResponseManage(
      dto,
      createTiendaPaquete,
      updateTiendaPaquete,
      navigate,
      dispatch,
      "tiendaPaquete",
      "addTiendaPaquete",
      "updateTiendaPaquete",
      paqueteId,
      (error: any) => {
        const status = error?.response?.status;
        if (status === 409) {
          notification.error({
            message:
              "Ya existe un paquete para ese nivel y grado en este colegio.",
            placement: "top",
          });
        } else {
          notification.error({
            message: "Error al guardar el paquete.",
            placement: "top",
          });
        }
      }
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 700 }}>
      <Title level={3}>
        {paqueteId ? "Editar Paquete Tienda" : "Crear Paquete Tienda"}
      </Title>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Colegio"
          name="school_id"
          rules={[{ required: true, message: "El colegio es requerido" }]}
        >
          <Select
            value={formValues.school_id}
            onChange={(v) =>
              setFormValues((p) => ({ ...p, school_id: v }))
            }
            options={tiendaColegios.map((c) => ({
              value: c.id,
              label: c.nombre,
            }))}
            placeholder="Seleccioná un colegio"
          />
        </Form.Item>

        <Form.Item
          label="Nivel"
          name="nivel"
          rules={[{ required: true, message: "El nivel es requerido" }]}
        >
          <Radio.Group
            value={formValues.nivel}
            onChange={(e) =>
              setFormValues((p) => ({ ...p, nivel: e.target.value }))
            }
          >
            <Radio value="PREESCOLAR">PREESCOLAR</Radio>
            <Radio value="PRIMARIA">PRIMARIA</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Grado"
          name="grado"
          rules={[{ required: true, message: "El grado es requerido" }]}
        >
          <InputNumber
            min={0}
            max={6}
            value={formValues.grado}
            onChange={(v) =>
              setFormValues((p) => ({ ...p, grado: v ?? 0 }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Título"
          name="titulo"
          rules={[
            { required: true, message: "El título es requerido" },
            { max: 120, message: "Máximo 120 caracteres" },
          ]}
        >
          <Input
            value={formValues.titulo}
            onChange={(e) =>
              setFormValues((p) => ({ ...p, titulo: e.target.value }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Descripción"
          name="descripcion"
          rules={[
            { required: true, message: "La descripción es requerida" },
            { max: 300, message: "Máximo 300 caracteres" },
          ]}
        >
          <TextArea
            rows={3}
            value={formValues.descripcion}
            onChange={(e) =>
              setFormValues((p) => ({ ...p, descripcion: e.target.value }))
            }
          />
        </Form.Item>

        <Form.Item
          label="Precio (MXN)"
          name="precio"
          rules={[{ required: true, message: "El precio es requerido" }]}
        >
          <InputNumber
            min={0}
            precision={0}
            value={formValues.precio}
            onChange={(v) =>
              setFormValues((p) => ({ ...p, precio: v ?? 0 }))
            }
          />
        </Form.Item>

        <Form.Item label="Imagen" name="imagen_url">
          <>
            {formValues.imagen_url && (
              <div style={{ marginBottom: 8 }}>
                <Image src={formValues.imagen_url} width={120} preview />
              </div>
            )}
            <Upload
              customRequest={async (options) => {
                setUploadingImg(true);
                setProgressImg(0);
                try {
                  const result = await customUpload(
                    {
                      ...options,
                      onProgress: ({ percent }: { percent: number }) => {
                        setProgressImg(Math.round(percent));
                        if (options.onProgress) options.onProgress({ percent });
                      },
                    },
                    "paquetes"
                  );
                  if (result?.url) {
                    form.setFieldsValue({ imagen_url: result.url });
                    setFormValues((p) => ({ ...p, imagen_url: result.url }));
                  }
                } finally {
                  setUploadingImg(false);
                }
              }}
              showUploadList={false}
              accept="image/jpeg,image/png,image/webp"
              disabled={uploadingImg}
            >
              <Button icon={<UploadOutlined />} loading={uploadingImg} aria-label="Subir imagen del paquete">
                {formValues.imagen_url ? "Cambiar imagen" : "Subir imagen"}
              </Button>
            </Upload>
            {uploadingImg && (
              <Progress percent={progressImg} size="small" style={{ marginTop: 8 }} />
            )}
          </>
        </Form.Item>

        <Form.Item
          label="Activo"
          name="activo"
          valuePropName="checked"
        >
          <Switch
            checked={formValues.activo}
            onChange={(v) =>
              setFormValues((p) => ({ ...p, activo: v }))
            }
          />
        </Form.Item>

        <Form.Item>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button onClick={() => navigate(-1)}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Guardar
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default TiendaPaqueteForm;
